/**
 * reteTassonomia — cuore ANTI-DUPLICATO della tassonomia della rete contatti.
 *
 * Modulo PURO e CONDIVISO (`shared/`): nessun accesso a Firestore, nessuno stato,
 * nessuna dipendenza dagli SDK. Vive qui — e non nella console — perché la
 * normalizzazione DEVE essere identica sui due lati:
 *  - la console la usa per non creare doppioni quando l'utente scrive un tag;
 *  - la Cloud Function la usa per validare/normalizzare ciò che propone l'AI.
 * Se le due divergessero, i duplicati rientrerebbero dalla finestra.
 * (Le functions lo ricevono via `npm run sync:shared` → `src/_shared-pkg/rete/`.)
 *
 * Le tre difese contro il tag sprawl, in ordine di robustezza:
 *  1. `slugTag()` — il doc id è lo slug normalizzato: due etichette che
 *     normalizzano allo stesso slug SONO lo stesso documento. Difesa fisica.
 *  2. `risolviTag()` — riconosce un tag anche quando lo si dice con un sinonimo
 *     (alias) invece della forma preferita.
 *  3. `trovaTagSimili()` — rete di sicurezza per i quasi-duplicati (singolare/
 *     plurale, refusi): non blocca, AVVISA prima di creare.
 *
 * Doc canonico: docs/01-ARCHITECTURE/RETE-CONTATTI-MODEL.md
 */

/**
 * Forma MINIMA di un tag per essere riconosciuto: solo ciò che serve al
 * confronto. Volutamente strutturale (non importa il tipo Firestore) così il
 * modulo funziona identico col client SDK e con l'Admin SDK.
 */
export interface TagRiconoscibile {
  /** Slug = doc id. */
  id: string;
  /** Forma preferita mostrata all'utente. */
  etichetta: string;
  /** Sinonimi già normalizzati. */
  alias?: string[];
  /** Slug della faccetta (non usato dal confronto, comodo per i chiamanti). */
  faccetta?: string;
}

/** Soglia di somiglianza (0-1) oltre la quale due etichette sono "quasi uguali". */
export const SOGLIA_SIMILARITA = 0.82;

/** Quanti candidati simili restituire al massimo. */
const MAX_SIMILI = 5;

/** Lunghezza minima perché il criterio "una contiene l'altra" possa scattare. */
const MIN_LEN_CONTENIMENTO = 5;

// ============================================================================
// NORMALIZZAZIONE
// ============================================================================

/**
 * Forma canonica di un testo per il CONFRONTO: minuscolo, senza diacritici,
 * senza punteggiatura, spazi collassati.
 *
 * "Opportunità di lavoro!" e "opportunita  DI lavoro" → "opportunita di lavoro".
 */
export function normalizzaTesto(raw: string): string {
  return raw
    .normalize('NFD')
    // Rimuove i segni diacritici combinanti (à → a, ö → o).
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Slug di un tag = doc id. Come `normalizzaTesto` ma con `_` al posto degli spazi.
 *
 * ⚠️ È la difesa strutturale contro i duplicati: se due etichette producono lo
 * stesso slug, scrivono lo stesso documento. Non esiste modo di creare due tag
 * "uguali" con nomi scritti in modo diverso.
 */
export function slugTag(etichetta: string): string {
  return normalizzaTesto(etichetta).replace(/\s/g, '_');
}

/**
 * Normalizza una lista di alias: forma canonica, senza vuoti, senza duplicati,
 * e senza l'etichetta stessa (sarebbe ridondante: lo slug la copre già).
 */
export function normalizzaAlias(alias: string[], etichetta: string): string[] {
  const etichettaNorm = normalizzaTesto(etichetta);
  const visti = new Set<string>();
  const out: string[] = [];
  for (const a of alias) {
    const norm = normalizzaTesto(a);
    if (!norm || norm === etichettaNorm || visti.has(norm)) continue;
    visti.add(norm);
    out.push(norm);
  }
  return out;
}

// ============================================================================
// SOMIGLIANZA
// ============================================================================

/**
 * Distanza di Levenshtein (numero minimo di modifiche di un carattere per
 * trasformare `a` in `b`). Due righe di matrice: memoria O(min(a,b)).
 */
export function distanzaLevenshtein(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;

  let precedente: number[] = Array.from({ length: b.length + 1 }, (_, i) => i);
  let corrente: number[] = new Array<number>(b.length + 1);

  for (let i = 1; i <= a.length; i++) {
    corrente[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const costoSostituzione = a[i - 1] === b[j - 1] ? 0 : 1;
      corrente[j] = Math.min(
        corrente[j - 1] + 1,              // inserimento
        precedente[j] + 1,                // cancellazione
        precedente[j - 1] + costoSostituzione,
      );
    }
    // Scambio delle righe: `corrente` diventa la precedente del giro dopo.
    const tmp = precedente;
    precedente = corrente;
    corrente = tmp;
  }
  return precedente[b.length];
}

/**
 * Somiglianza 0-1 tra due testi (1 = identici dopo normalizzazione).
 * Cattura singolare/plurale ("fornitore"/"fornitori") e i refusi.
 */
export function somiglianza(a: string, b: string): number {
  const na = normalizzaTesto(a);
  const nb = normalizzaTesto(b);
  if (!na && !nb) return 1;
  if (!na || !nb) return 0;
  if (na === nb) return 1;
  const maxLen = Math.max(na.length, nb.length);
  return 1 - distanzaLevenshtein(na, nb) / maxLen;
}

// ============================================================================
// RISOLUZIONE — l'etichetta detta a voce → il tag che esiste già
// ============================================================================

/**
 * Trova il tag che corrisponde a un'etichetta, considerando anche gli alias.
 * È il passo che evita di creare `mi potrebbe assumere` quando esiste già
 * `opportunità di lavoro` con quell'alias.
 *
 * ⚠️ Confronta l'ETICHETTA oltre allo slug, e non solo per eleganza: dopo una
 * RINOMINA il doc id resta quello storico (`opportunita_lavoro`) mentre
 * l'etichetta è cambiata (`opportunità di lavoro` → slug `opportunita_di_lavoro`).
 * Fidarsi del solo slug farebbe rientrare i duplicati dalla finestra.
 *
 * @returns il tag, oppure `null` se nessuno corrisponde.
 */
export function risolviTag<T extends TagRiconoscibile>(
  etichetta: string,
  tagEsistenti: T[],
): T | null {
  const slug = slugTag(etichetta);
  const norm = normalizzaTesto(etichetta);
  if (!slug) return null;

  // 1. Match sullo slug (identità del documento).
  const perSlug = tagEsistenti.find((t) => t.id === slug);
  if (perSlug) return perSlug;

  // 2. Match sull'etichetta normalizzata (regge le rinomine).
  const perEtichetta = tagEsistenti.find((t) => normalizzaTesto(t.etichetta) === norm);
  if (perEtichetta) return perEtichetta;

  // 3. Match su un alias già registrato.
  const perAlias = tagEsistenti.find((t) => (t.alias ?? []).includes(norm));
  if (perAlias) return perAlias;

  return null;
}

/** Un candidato "quasi uguale" a ciò che si sta creando. */
export interface TagSimile<T extends TagRiconoscibile = TagRiconoscibile> {
  tag: T;
  /** Somiglianza 0-1 col testo cercato. */
  punteggio: number;
  /** Cosa ha fatto scattare il sospetto (utile da mostrare nella UI). */
  motivo: 'etichetta' | 'alias' | 'contenuto';
}

/**
 * Candidati simili a un'etichetta che si sta per creare — RETE DI SICUREZZA per
 * i quasi-duplicati che lo slug non intercetta ("fornitori" vs "fornitore").
 *
 * NON blocca la creazione: serve a mostrare "esiste già qualcosa di simile,
 * vuoi usare quello?". La decisione resta umana.
 */
export function trovaTagSimili<T extends TagRiconoscibile>(
  etichetta: string,
  tagEsistenti: T[],
): TagSimile<T>[] {
  const norm = normalizzaTesto(etichetta);
  if (!norm) return [];
  const slug = slugTag(etichetta);

  const candidati: TagSimile<T>[] = [];
  for (const tag of tagEsistenti) {
    // Il match esatto non è un "simile": lo gestisce risolviTag().
    if (tag.id === slug) continue;

    const puntEtichetta = somiglianza(etichetta, tag.etichetta);
    let migliore: TagSimile<T> | null =
      puntEtichetta >= SOGLIA_SIMILARITA
        ? { tag, punteggio: puntEtichetta, motivo: 'etichetta' }
        : null;

    // Un alias molto simile conta come l'etichetta.
    for (const alias of tag.alias ?? []) {
      const punt = somiglianza(etichetta, alias);
      if (punt >= SOGLIA_SIMILARITA && (!migliore || punt > migliore.punteggio)) {
        migliore = { tag, punteggio: punt, motivo: 'alias' };
      }
    }

    // Contenimento: "fotovoltaico residenziale" contiene "fotovoltaico".
    // Solo per testi non banali, per non far scattare tutto su parole corte.
    if (!migliore && norm.length >= MIN_LEN_CONTENIMENTO) {
      const normTag = normalizzaTesto(tag.etichetta);
      if (
        normTag.length >= MIN_LEN_CONTENIMENTO &&
        (norm.includes(normTag) || normTag.includes(norm))
      ) {
        migliore = { tag, punteggio: 0.8, motivo: 'contenuto' };
      }
    }

    if (migliore) candidati.push(migliore);
  }

  candidati.sort((a, b) => b.punteggio - a.punteggio);
  return candidati.slice(0, MAX_SIMILI);
}

// ============================================================================
// RAGGRUPPAMENTO PER FACCETTA — per la UI e per il prompt dell'AI
// ============================================================================

/**
 * Raggruppa i tag per slug di faccetta, ordinati per etichetta (locale it).
 * Usato sia dai filtri della console sia dalla costruzione del prompt.
 */
export function raggruppaPerFaccetta<T extends TagRiconoscibile>(
  tag: T[],
): Record<string, T[]> {
  const out: Record<string, T[]> = {};
  for (const t of tag) {
    const faccetta = t.faccetta ?? '';
    if (!out[faccetta]) out[faccetta] = [];
    out[faccetta].push(t);
  }
  for (const gruppo of Object.values(out)) {
    gruppo.sort((a, b) => a.etichetta.localeCompare(b.etichetta, 'it', { sensitivity: 'base' }));
  }
  return out;
}
